/**
 * Frontend Security Utilities
 * Handles CSRF tokens, secure API calls, and XSS protection
 */

import DOMPurify from 'dompurify';
import logger from './logger';

// ============ CSRF PROTECTION ============

/**
 * Get CSRF token from cookie
 */
export const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * Add CSRF token to request headers
 */
export const withCSRF = (headers = {}) => {
  const csrfToken = getCSRFToken();
  if (csrfToken) {
    return {
      ...headers,
      'X-CSRF-Token': csrfToken,
    };
  }
  return headers;
};

// ============ XSS PROTECTION ============

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - Potentially unsafe HTML string
 * @param {object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  
  const defaultOptions = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  };

  const config = { ...defaultOptions, ...options };
  
  // Configure DOMPurify to add rel="noopener noreferrer" to all links
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('rel', 'noopener noreferrer');
      if (node.getAttribute('target') === '_blank') {
        // Already has target="_blank", ensure rel is set
      }
    }
  });

  const clean = DOMPurify.sanitize(dirty, config);
  
  // Remove the hook after use to prevent memory leaks
  DOMPurify.removeHook('afterSanitizeAttributes');
  
  return clean;
};

/**
 * Sanitize plain text (escape HTML entities)
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text safe for display
 */
export const escapeHTML = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Sanitize URL to prevent javascript: and data: schemes
 * @param {string} url - URL to sanitize
 * @returns {string|null} - Safe URL or null if unsafe
 */
export const sanitizeURL = (url) => {
  if (!url || typeof url !== 'string') {
    return null;
  }
  
  try {
    const parsed = new URL(url, window.location.origin);
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    
    if (!allowedProtocols.includes(parsed.protocol)) {
      logger.security('blocked_unsafe_url', { url: url.substring(0, 50) });
      return null;
    }
    
    return parsed.href;
  } catch {
    // Relative URL or invalid - allow relative URLs
    if (url.startsWith('/') && !url.startsWith('//')) {
      return url;
    }
    return null;
  }
};

// ============ SECURE FETCH WRAPPER ============

/**
 * Secure fetch wrapper with CSRF token and credentials
 */
export const secureFetch = async (url, options = {}) => {
  const defaultOptions = {
    credentials: 'include', // Always include cookies
    headers: {
      'Content-Type': 'application/json',
      ...withCSRF(options.headers || {}),
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, mergedOptions);
    
    // Check for new CSRF token in response headers
    const newCSRFToken = response.headers.get('X-CSRF-Token');
    if (newCSRFToken) {
      // Token rotation handled by server via cookies
      logger.debug('CSRF token rotated');
    }

    return response;
  } catch (error) {
    logger.error('Secure fetch failed:', error.message);
    throw error;
  }
};

// ============ INPUT VALIDATION ============

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 */
export const validatePassword = (password) => {
  const errors = [];
  if (password.length < 8) {
    errors.push('Parola trebuie să aibă minim 8 caractere');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Parola trebuie să conțină cel puțin o literă mare');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Parola trebuie să conțină cel puțin o literă mică');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Parola trebuie să conțină cel puțin o cifră');
  }
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Sanitize user input (trim and limit length)
 */
export const sanitizeInput = (input, maxLength = 1000) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  return input.trim().substring(0, maxLength);
};

export default {
  getCSRFToken,
  withCSRF,
  sanitizeHTML,
  escapeHTML,
  sanitizeURL,
  secureFetch,
  isValidEmail,
  validatePassword,
  sanitizeInput,
};
